// vignette — soft radial darkening at the corners. Per-pixel multiply blend
// using a radial falloff from center.
//
// We intentionally don't use ctx.createRadialGradient + fillRect with
// composite='multiply' because the per-pixel math is identical, easier to
// verify in goldens, and dodges ctx.filter / blend-mode discrepancies between
// browsers.

import {
  type FilterContext,
  eachPixel,
  getImageData,
  multiplyBlend,
  putImageData,
} from './_helpers'

export type VignetteOpts = {
  // Strength at corners, 0..1. Default 0.3 → corners darken to ~70% of center.
  strength?: number
}

// Default strength bumped 0.3 → 0.4 after PMF "beef up the filter" feedback.
// Corners now darken to ~60% of center (was ~70%). Test "no-op at strength=0"
// still passes; the corner-darkening test only checks `< 200`, so it's robust.
export function vignette(c: FilterContext, opts: VignetteOpts = {}): void {
  const strength = opts.strength ?? 0.4
  const cx = c.width / 2
  const cy = c.height / 2
  // Distance from center to corner is the falloff radius.
  const maxDist = Math.sqrt(cx * cx + cy * cy)

  const img = getImageData(c.ctx)
  const width = c.width

  eachPixel(img, (r, g, b, a, i) => {
    const pixelIdx = i / 4
    const px = pixelIdx % width
    const py = Math.floor(pixelIdx / width)
    const dx = px - cx
    const dy = py - cy
    const d = Math.sqrt(dx * dx + dy * dy) / maxDist
    // Quadratic falloff feels closer to a real lens vignette than linear.
    const t = d * d
    // overlay alpha: 0 at center, `strength` at corners.
    const alpha = t * strength
    // Multiply by (255 - alpha*255) — i.e. darken proportionally.
    const dark = 255 - alpha * 255
    return [multiplyBlend(r, dark), multiplyBlend(g, dark), multiplyBlend(b, dark), a]
  })
  putImageData(c.ctx, img)
}
